/**
 * 3D draw overlay for in-progress shape preview.
 * Uses Polygon3DElement/Polyline3DElement during drawing.
 * Circles are approximated as polygons since Maps 3D has no native circle.
 */

import type { GeoPoint, DrawingTool } from '@vtak/shared';
import { circleToPolygonPoints, rectangleFromCorners } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';
import { CIRCLE_POLYGON_SEGMENTS } from '@/constants/drawing';

/** Overlay stroke color */
const OVERLAY_STROKE = '#FFD700';

/** Active overlay elements */
let overlayPolygon: google.maps.maps3d.Polygon3DElement | null = null;
let overlayPolyline: google.maps.maps3d.Polyline3DElement | null = null;

/** Active map reference for cleanup */
let activeMap3d: google.maps.maps3d.Map3DElement | null = null;

/** Show the 3D draw overlay */
export function showDrawOverlay3D(
  map3d: google.maps.maps3d.Map3DElement,
  vertices: GeoPoint[],
  tool: DrawingTool,
): void {
  clearDrawOverlay3D();
  activeMap3d = map3d;
  renderOverlay3D(map3d, vertices, tool);
}

/** Update the 3D draw overlay */
export function updateDrawOverlay3D(
  map3d: google.maps.maps3d.Map3DElement,
  vertices: GeoPoint[],
  tool: DrawingTool,
): void {
  clearDrawOverlay3D();
  activeMap3d = map3d;
  renderOverlay3D(map3d, vertices, tool);
}

/** Remove all 3D draw overlay elements */
export function clearDrawOverlay3D(): void {
  if (overlayPolygon && activeMap3d) {
    try { activeMap3d.removeChild(overlayPolygon); } catch { /* already removed */ }
    overlayPolygon = null;
  }
  if (overlayPolyline && activeMap3d) {
    try { activeMap3d.removeChild(overlayPolyline); } catch { /* already removed */ }
    overlayPolyline = null;
  }
}

/** Render the appropriate 3D overlay geometry */
function renderOverlay3D(
  map3d: google.maps.maps3d.Map3DElement,
  vertices: GeoPoint[],
  tool: DrawingTool,
): void {
  if (isCircleTool(tool)) {
    renderCircle3D(map3d, vertices);
    return;
  }

  if (tool === 'rectangle' && vertices.length >= 2) {
    renderPolygon3D(map3d, rectangleFromCorners(vertices[0]!, vertices[1]!));
    return;
  }

  if (isPolygonTool(tool) && vertices.length >= 3) {
    renderPolygon3D(map3d, vertices);
    return;
  }

  if (vertices.length >= 2) {
    renderPolyline3D(map3d, vertices);
  }
}

/** Render a circle as a polygon approximation in 3D */
function renderCircle3D(
  map3d: google.maps.maps3d.Map3DElement,
  vertices: GeoPoint[],
): void {
  if (vertices.length === 0) return;
  const center = vertices[0]!;
  const radius = useDrawingStore.getState().radiusMeters;
  if (radius <= 0) return;

  const circlePoints = circleToPolygonPoints(center, radius, CIRCLE_POLYGON_SEGMENTS);
  renderPolygon3D(map3d, circlePoints);
}

/** Render a closed polygon in 3D */
function renderPolygon3D(
  map3d: google.maps.maps3d.Map3DElement,
  vertices: GeoPoint[],
): void {
  const coordinates = verticesToCoords(vertices);
  overlayPolygon = new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: coordinates,
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor: OVERLAY_STROKE,
    strokeWidth: 2,
    fillColor: 'rgba(255, 215, 0, 0.15)',
    drawsOccludedSegments: true,
  });
  map3d.appendChild(overlayPolygon);
}

/** Render an open polyline in 3D */
function renderPolyline3D(
  map3d: google.maps.maps3d.Map3DElement,
  vertices: GeoPoint[],
): void {
  const coordinates = verticesToCoords(vertices);
  overlayPolyline = new google.maps.maps3d.Polyline3DElement({
    coordinates,
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor: OVERLAY_STROKE,
    strokeWidth: 2,
    drawsOccludedSegments: true,
  });
  map3d.appendChild(overlayPolyline);
}

/** Convert GeoPoint array to LatLngAltitudeLiteral array */
function verticesToCoords(
  vertices: GeoPoint[],
): google.maps.LatLngAltitudeLiteral[] {
  return vertices.map((v) => ({ lat: v.lat, lng: v.lon, altitude: 0 }));
}

/** Check if tool renders as a circle */
function isCircleTool(tool: DrawingTool): boolean {
  return tool === 'circle' || tool === 'geofence';
}

/** Check if tool renders as a closed polygon */
function isPolygonTool(tool: DrawingTool): boolean {
  return tool === 'polygon' || tool === 'freeform';
}
